import { type LocaleDefinition } from '@solinkcorp/locale';
import { type NavTabDescriptor } from 'components/NavigationFrame/DesktopNav/NavTab';
import { useMemo } from 'react';
import { CLIPS_PAGE, INVESTIGATIONS_PAGE } from 'vd-constants';

/**
 * Sidebar sections in render order, matching the navigation design.
 *
 * Grouping is presentation only — it never affects whether an item is visible.
 * Visibility stays on `NavTabDescriptor.show`, which is also what builds the
 * route table, so the nav and the router cannot disagree about what a user is
 * allowed to reach.
 */
export const NAV_GROUP_ORDER = ['main', 'monitor', 'investigate', 'workspace'] as const;

export type NavGroupId = (typeof NAV_GROUP_ORDER)[number];

/**
 * Aspen investigate-section item order. `generateMenu` keeps Investigations last
 * so flag-off DesktopNav matches develop; this restores Investigations above
 * Clips when bucketing for the Aspen rail.
 */
const INVESTIGATE_ITEM_ORDER = [INVESTIGATIONS_PAGE, CLIPS_PAGE] as const;

function orderGroupItems<T extends NavTabDescriptor>(id: NavGroupId, items: T[]): T[] {
  if (id !== 'investigate' || items.length < 2) return items;
  return [...items].sort((a, b) => {
    const aRank = INVESTIGATE_ITEM_ORDER.indexOf(a.page as (typeof INVESTIGATE_ITEM_ORDER)[number]);
    const bRank = INVESTIGATE_ITEM_ORDER.indexOf(b.page as (typeof INVESTIGATE_ITEM_ORDER)[number]);
    const aOrder = aRank === -1 ? INVESTIGATE_ITEM_ORDER.length : aRank;
    const bOrder = bRank === -1 ? INVESTIGATE_ITEM_ORDER.length : bRank;
    return aOrder - bOrder;
  });
}

/** Section heading. The first group is deliberately unlabelled in the design. */
export function getNavGroupLabel(id: NavGroupId, locale: LocaleDefinition): string | undefined {
  switch (id) {
    case 'monitor':
      return locale.navigation.groupMonitor;
    case 'investigate':
      return locale.navigation.groupInvestigate;
    case 'workspace':
      return locale.navigation.groupWorkspace;
    case 'main':
    default:
      return undefined;
  }
}

export interface NavGroup<T extends NavTabDescriptor> {
  id: NavGroupId;
  label?: string;
  items: T[];
}

/**
 * Bucket already-filtered nav tabs into their sidebar sections, dropping any
 * section left empty by the user's permissions.
 *
 * Items without an explicit `group` fall into `main` so a newly added tab still
 * renders rather than silently disappearing.
 */
export function useNavGroups<T extends NavTabDescriptor>(
  tabs: T[],
  locale: LocaleDefinition,
): NavGroup<T>[] {
  return useMemo(
    () =>
      NAV_GROUP_ORDER.map((id) => ({
        id,
        label: getNavGroupLabel(id, locale),
        items: orderGroupItems(
          id,
          tabs.filter((tab) => (tab.group ?? 'main') === id),
        ),
      })).filter((group) => group.items.length > 0),
    [tabs, locale],
  );
}
